/**
 * File Upload Hook
 * Reusable hook for file uploading with validation, progress tracking, and error handling
 */

import { useState, useCallback } from "react";
import { uploadFile } from "../api/upload-api";
import type { UploadFileParams, UploadFileResponse } from "../api/upload-api";
import {
  validateFile,
  generateFileTitle,
  type FileValidationOptions,
} from "../utils/file-utils";

/**
 * Upload state for single file
 */
export interface UploadState {
  file: File;
  status: "idle" | "uploading" | "success" | "error";
  progress: number;
  error?: string;
  response?: UploadFileResponse;
}

/**
 * Hook options
 */
export interface UseFileUploadOptions {
  isInput?: boolean; // true for generation inputs
  isWardrobe?: boolean; // true for wardrobe uploads
  tags?: string[]; // Optional tags for uploads
  validation?: FileValidationOptions; // File validation options
  onSuccess?: (response: UploadFileResponse, file: File) => void;
  onError?: (error: string, file: File) => void;
}

/**
 * Hook return type
 */
export interface UseFileUploadReturn {
  // State
  uploads: UploadState[];
  isUploading: boolean;
  hasErrors: boolean;

  // Actions
  uploadSingleFile: (
    file: File,
    options?: Partial<UploadFileParams>
  ) => Promise<UploadFileResponse | null>;
  uploadFiles: (
    files: File[],
    options?: Partial<UploadFileParams>
  ) => Promise<UploadFileResponse[]>;
  clearUploads: () => void;
  removeUpload: (file: File) => void;
}

/**
 * Reusable File Upload Hook
 * @param options - Hook configuration options
 * @returns Upload state and actions
 */
export const useFileUpload = (
  options: UseFileUploadOptions = {}
): UseFileUploadReturn => {
  const { isInput, isWardrobe, tags, validation, onSuccess, onError } = options;

  // ✅ Upload states
  const [uploads, setUploads] = useState<UploadState[]>([]);

  // ✅ Computed states
  const isUploading = uploads.some((u) => u.status === "uploading");
  const hasErrors = uploads.some((u) => u.status === "error");

  /**
   * Upload single file
   */
  const uploadSingleFile = useCallback(
    async (
      file: File,
      uploadOptions: Partial<UploadFileParams> = {}
    ): Promise<UploadFileResponse | null> => {
      // ✅ Validate file
      const validationResult = validateFile(file, validation);
      if (!validationResult.valid) {
        // Validation failed
        const errorMessage = validationResult.error || "Dosya geçersiz";

        setUploads((prev) => [
          ...prev,
          {
            file,
            status: "error",
            progress: 0,
            error: errorMessage,
          },
        ]);

        if (onError) {
          onError(errorMessage, file);
        }

        return null;
      }

      // ✅ Add to upload queue
      setUploads((prev) => [
        ...prev,
        {
          file,
          status: "uploading",
          progress: 0,
        },
      ]);

      try {
        // ✅ Upload file
        const response = await uploadFile({
          file,
          title: uploadOptions.title || generateFileTitle(file),
          isInput: uploadOptions.isInput ?? isInput,
          isWardrobe: uploadOptions.isWardrobe ?? isWardrobe,
          tags: uploadOptions.tags ?? tags,
        });

        // ✅ Success
        setUploads((prev) =>
          prev.map((u) =>
            u.file === file
              ? {
                  ...u,
                  status: "success",
                  progress: 100,
                  response,
                }
              : u
          )
        );

        if (onSuccess) {
          onSuccess(response, file);
        }

        return response;
      } catch (error: any) {
        // ✅ Error
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Dosya yüklenemedi";

        setUploads((prev) =>
          prev.map((u) =>
            u.file === file
              ? {
                  ...u,
                  status: "error",
                  progress: 0,
                  error: errorMessage,
                }
              : u
          )
        );

        if (onError) {
          onError(errorMessage, file);
        }

        return null;
      }
    },
    [isInput, isWardrobe, tags, validation, onSuccess, onError]
  );

  /**
   * Upload multiple files
   */
  const uploadFiles = useCallback(
    async (
      files: File[],
      uploadOptions: Partial<UploadFileParams> = {}
    ): Promise<UploadFileResponse[]> => {
      // ✅ Upload files sequentially
      const results: UploadFileResponse[] = [];

      for (const file of files) {
        const result = await uploadSingleFile(file, uploadOptions);
        if (result) {
          results.push(result);
        }
      }

      return results;
    },
    [uploadSingleFile]
  );

  /**
   * Clear all uploads
   */
  const clearUploads = useCallback(() => {
    setUploads([]);
  }, []);

  /**
   * Remove single upload
   */
  const removeUpload = useCallback((file: File) => {
    setUploads((prev) => prev.filter((u) => u.file !== file));
  }, []);

  return {
    // State
    uploads,
    isUploading,
    hasErrors,

    // Actions
    uploadSingleFile,
    uploadFiles,
    clearUploads,
    removeUpload,
  };
};
